import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
  withGoogleMapsState,
} from '@atom/components/common/map/hooks/googleMapsStateHook';
import Map from '@atom/components/common/map/Map';
import MapMarkers from '@atom/components/common/map/MapMarkers';
import MapPinMarker from '@atom/components/common/map/markers/MapPinMarker';
import { USER_UPDATE } from '@atom/graph/user';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import { getLoggedInUserMapLocationSelector } from '@atom/selectors/mapSelectors';
import {
  LocationUpdatePayload,
  MapEditMetadata,
  MapEditMetadataType,
  MapParams,
} from '@atom/types/map';
import { UserDetail, UserUpdateInput } from '@atom/types/user';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  getGeoJsonFromMapBounds,
  getGoogleShapeCenter,
  getMapCenterAndZoomParams,
  getMapEditMetadata,
  urlValueToLatLng,
} from '@atom/utilities/mapUtilities';

import UserContext from '../UserContext';

const USER_DETAIL_ZOOM = 14;

const UserDetailMap = () => {
  const currentUserLocation = useSelector(getLoggedInUserMapLocationSelector);

  const dispatch = useGoogleMapsStateUpdater();
  const { isLoaded } = useGoogleMapsStateValue();
  const preferences = usePreferences();

  const { user, refetch } = useContext(UserContext);

  const [map, setMap] = useState<google.maps.Map>(null);
  const [coordinates, setCoordinates] = useState<string>(null);

  const [updateUser, { loading: updateUserLoading }] = useMutation<
    { userUpdate: UserDetail },
    { input: UserUpdateInput }
  >(USER_UPDATE, {
    onCompleted: () => {
      refetch();
    },
  });

  useEffect(() => {
    dispatch({
      type: 'SET',
      payload: {
        key: 'grab',
        data: false,
      },
    });
  }, []);

  const [{ center, zoom, searchPoint }, { updateSearchParams }] = useNavigation(
    {
      center: SearchParamType.STRING,
      zoom: SearchParamType.NUMBER,
      searchPoint: SearchParamType.STRING,
    },
  );

  useEffect(() => {
    const centerShape = getGoogleShapeCenter(user?.location);
    updateSearchParams({
      zoom: USER_DETAIL_ZOOM,
      center: `${centerShape?.lat},${centerShape?.lng}`,
    });
  }, []);

  const onLoad = useCallback(setMap, []);

  const onIdle = useCallback(() => {
    try {
      const existingMapParams: MapParams = {
        center,
        zoom,
        searchPoint,
      };

      const mapParams = getMapCenterAndZoomParams(
        map,
        existingMapParams,
        preferences,
        currentUserLocation,
      );

      if (!R.equals(existingMapParams, mapParams) || !coordinates) {
        updateSearchParams({
          zoom: mapParams.zoom,
          center: mapParams.center,
        });
        R.pipe(
          getGeoJsonFromMapBounds,
          R.prop('coordinates'),
          JSON.stringify,
          setCoordinates,
        )(map);
      }
    } catch {
      updateSearchParams({ zoom: null, center: null });
    }
  }, [
    map,
    zoom,
    center,
    searchPoint,
    coordinates,
    preferences,
    updateSearchParams,
  ]);

  const onSave = (payload: LocationUpdatePayload) => {
    updateUser({
      variables: {
        input: {
          id: user.id,
          location: payload?.location,
        },
      },
    });
  };

  const canUpdateLocation = hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  const mapEditMetadata: MapEditMetadata[] = useMemo(
    () => [
      getMapEditMetadata(user, canUpdateLocation, MapEditMetadataType.USER),
    ],
    [user],
  );

  const mapData = { users: [user] };

  return !isLoaded ? (
    <Progress style={{ height: '100%' }} />
  ) : (
    <Map
      onIdle={onIdle}
      onLoad={onLoad}
      center={center && urlValueToLatLng(center)}
      zoom={zoom}
      mapStyleControl
      layerControls
      mapSearchBox
      editControls
      mapEditMetadata={mapEditMetadata}
      onSave={onSave}
      locationEditZoom={USER_DETAIL_ZOOM}
    >
      {!updateUserLoading && <MapMarkers onClick={() => {}} data={mapData} />}
      {searchPoint && <MapPinMarker center={searchPoint} />}
    </Map>
  );
};

export default withGoogleMapsState(UserDetailMap);
